/**
 * Leviathan - Customer Type Templates List Page
 * Quasar Prime LLC
 *
 * Admin interface for managing customer type templates
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Plus, Trash2, Edit, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { API_URL } from '@/config/api';

interface TemplateProduct {
  id: string;
  productId: string;
  defaultMargin: number | null;
  product: {
    id: string;
    name: string;
    sku: string;
  };
}

interface Template {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  products: TemplateProduct[];
  _count: {
    customers: number;
  };
}

export default function CustomerTypeTemplatesPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, [accessToken]);

  const fetchTemplates = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customer-type-templates`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch templates');
      const data = await response.json();
      setTemplates(data.templates);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_URL}/api/customer-type-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json();
        const msg =
          data?.error?.message || data?.message || 'Failed to delete template';
        throw new Error(msg);
      }
      setDeleteConfirmId(null);
      fetchTemplates();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete template');
      setDeleteConfirmId(null);
    }
  };

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 via-blue-500 to-amber-500" />
          <h1 className="text-3xl font-bold text-slate-100 pl-6">
            Customer Type Templates
          </h1>
          <p className="text-slate-400 text-sm pl-6 mt-1">
            {templates.length} template{templates.length !== 1 ? 's' : ''} - Define
            product sets for customer categories
          </p>
        </div>

        <Link
          to="/admin/customer-type-templates/new"
          className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium py-2 px-4 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>New Template</span>
        </Link>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500" />

        <div className="bg-slate-800 border border-slate-700 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-600 mb-4" />
              <div className="text-slate-400 mb-4">No templates found</div>
              <Link
                to="/admin/customer-type-templates/new"
                className="text-amber-500 hover:text-amber-400 text-sm"
              >
                Create your first template
              </Link>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-900 border-b border-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium w-8" />
                  <th className="text-left px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Name
                  </th>
                  <th className="text-left px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Description
                  </th>
                  <th className="text-center px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Products
                  </th>
                  <th className="text-center px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Customers
                  </th>
                  <th className="text-center px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <>
                    <tr
                      key={template.id}
                      className="border-b border-slate-700 hover:bg-amber-500/5 cursor-pointer"
                      onClick={() =>
                        navigate(`/admin/customer-type-templates/${template.id}`)
                      }
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => toggleExpand(template.id, e)}
                          className="text-slate-400 hover:text-amber-500 transition-colors"
                        >
                          {expandedId === template.id ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-100 font-medium font-mono">
                          {template.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {template.description || (
                          <span className="text-slate-600 italic">No description</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/50 font-mono">
                          {template.products.length}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-400 text-sm font-mono">
                        {template._count.customers}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-1 text-xs border ${
                            template.isActive
                              ? 'bg-green-500/20 text-green-400 border-green-500/50'
                              : 'bg-slate-500/20 text-slate-400 border-slate-500/50'
                          }`}
                        >
                          {template.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/admin/customer-type-templates/${template.id}`
                              );
                            }}
                            className="p-1 text-slate-400 hover:text-amber-500 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmId(template.id);
                            }}
                            className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded products row */}
                    {expandedId === template.id && (
                      <tr
                        key={`${template.id}-expanded`}
                        className="border-b border-slate-700 bg-slate-900/50"
                      >
                        <td colSpan={7} className="px-8 py-4">
                          {template.products.length === 0 ? (
                            <div className="text-slate-500 text-sm italic">
                              No products assigned to this template
                            </div>
                          ) : (
                            <div>
                              <div className="text-slate-400 text-xs uppercase tracking-wider mb-2 font-medium">
                                Assigned Products
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {template.products.map((tp) => (
                                  <div
                                    key={tp.id}
                                    className="flex items-center justify-between bg-slate-800 border border-slate-700 px-3 py-2"
                                  >
                                    <div>
                                      <span className="text-slate-100 text-sm font-medium">
                                        {tp.product.name}
                                      </span>
                                      <span className="text-slate-500 text-xs ml-2 font-mono">
                                        {tp.product.sku}
                                      </span>
                                    </div>
                                    {tp.defaultMargin != null && (
                                      <span className="text-amber-500 text-xs font-mono">
                                        {tp.defaultMargin}% margin
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="relative bg-slate-800 border border-slate-700 p-6 max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-red-500" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-red-500" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-red-500" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-red-500" />

            <h3 className="text-lg font-bold text-slate-100 mb-2">
              Confirm Deletion
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Are you sure you want to delete this template? This action cannot be
              undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white transition-colors text-sm"
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
